// useThreeScene.js
import { useEffect, useRef, useCallback } from 'react';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import { createPerspectiveCamera } from '../utils/rendering/lighting';

// Function to recursively dispose of Three.js resources
const disposeThreeJSObject = (obj) => {
  if (!obj) return;
  
  // Handle arrays like scene.children
  if (Array.isArray(obj)) {
    obj.forEach(child => disposeThreeJSObject(child));
    return;
  }

  // Dispose of geometries
  if (obj.geometry) {
    obj.geometry.dispose();
  }

  // Dispose of materials
  if (obj.material) {
    if (Array.isArray(obj.material)) {
      obj.material.forEach(material => {
        if (material.map) material.map.dispose();
        material.dispose();
      });
    } else {
      if (obj.material.map) obj.material.map.dispose();
      obj.material.dispose();
    }
  }

  // Recursively handle children
  if (obj.children && obj.children.length > 0) {
    obj.children.forEach(child => disposeThreeJSObject(child));
  }
};

const useThreeScene = (onSceneUpdate) => {
  const mountRef = useRef(null);
  const sceneRef = useRef(new THREE.Scene());
  const cameraRef = useRef(null);
  const rendererRef = useRef(null);
  const controlsRef = useRef(null);
  const animationFrameRef = useRef(null);

  const initialize = useCallback(() => {
    const mount = mountRef.current;
    const renderer = new THREE.WebGLRenderer({ 
      antialias: true,
      alpha: true,
      powerPreference: 'high-performance'
    });
    renderer.setSize(window.innerWidth, window.innerHeight);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2)); // Limit pixel ratio for performance
    mount.appendChild(renderer.domElement);
    rendererRef.current = renderer;

    const aspect = window.innerWidth / window.innerHeight;
    const camera = createPerspectiveCamera(aspect);
    cameraRef.current = camera;

    controlsRef.current = new OrbitControls(camera, renderer.domElement);
    
    // Building dimensions based on geometry code
    const width = 42;
    const depth = 21; // Default depth
    
    // Calculate building center for orbit controls target
    const centerX = width / 2;
    const centerY = 15; // Approximate vertical center of typical building
    const centerZ = depth / 2;
    
    // Set the target of the orbit controls to the building's center
    // Adjust target X position to better center the building
    controlsRef.current.target.set(centerX, centerY, centerZ);
    
    controlsRef.current.enableDamping = true; // Smoother camera movement
    controlsRef.current.dampingFactor = 0.05;
    
    // Update camera initial position to match the new perspective
    camera.position.set(centerX + 30, centerY + 40, centerZ + 120);
    camera.lookAt(centerX, centerY, centerZ);
    
    // Add some more helpful orbit control settings
    controlsRef.current.minDistance = 10; // Minimum zoom distance
    controlsRef.current.maxDistance = 500; // Maximum zoom distance
    controlsRef.current.maxPolarAngle = Math.PI * 0.85; // Limit vertical rotation

    // Basic scene setup (lights, background, etc.) could go here
    sceneRef.current.background = new THREE.Color(0xdfe2e8);

    const animate = () => {
      animationFrameRef.current = requestAnimationFrame(animate);
      controlsRef.current.update(); // Update controls
      renderer.render(sceneRef.current, camera);
    };
    animate();
  }, []);

  useEffect(() => {
    initialize();
    const handleResize = () => {
      const aspect = window.innerWidth / window.innerHeight;
      cameraRef.current.aspect = aspect;
      cameraRef.current.updateProjectionMatrix();
      rendererRef.current.setSize(window.innerWidth, window.innerHeight);
    };
    window.addEventListener('resize', handleResize);
    
    // Improved cleanup function
    return () => {
      window.removeEventListener('resize', handleResize);
      
      // Cancel animation frame
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      
      // Dispose of all scene objects
      if (sceneRef.current) {
        disposeThreeJSObject(sceneRef.current.children);
      }
      
      // Dispose of controls
      if (controlsRef.current) {
        controlsRef.current.dispose();
      }
      
      // Dispose of renderer
      if (rendererRef.current) {
        if (mountRef.current) {
          mountRef.current.removeChild(rendererRef.current.domElement);
        }
        rendererRef.current.dispose();
      }
    };
  }, [initialize]);

  // Allow external scene updates
  useEffect(() => {
    if (onSceneUpdate) onSceneUpdate(sceneRef.current);
  }, [onSceneUpdate]);

  return { mountRef, scene: sceneRef.current, camera: cameraRef.current };
};

export default useThreeScene;